import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, ShieldAlert, Wrench } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PersistentPasswordRotation — non-dismissible password change modal
 * triggered by admin "Force Password Change" or app-wide reset.
 *
 * Key rules (admin-stated):
 *   • Heavy blur backdrop, no exit controls
 *   • 2-minute countdown — expiry forces logout
 *   • Page refresh during rotation forces logout
 *   • New password must satisfy the user's role policy (server-side
 *     enforcement; we surface inline errors)
 *   • Requires current password + new password (twice) + TOTP if user
 *     has one
 */
const SESSION_FLAG = '__orb_rotation_in_progress';
const ROTATION_TTL_MS = 120 * 1000;

export default function PersistentPasswordRotation() {
  const { user, refreshUser, api, logout } = useAuth();
  const required = user?.password_rotation_required;
  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [totp, setTotp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Refresh-detection: if `SESSION_FLAG` already exists on mount and
  // we're being asked to rotate again → user refreshed mid-flow.
  // Force logout per spec.
  useEffect(() => {
    if (!required) return;
    const flag = sessionStorage.getItem(SESSION_FLAG);
    if (flag) {
      sessionStorage.removeItem(SESSION_FLAG);
      toast.error('Session ended — please log back in to continue');
      logout();
      return;
    }
    sessionStorage.setItem(SESSION_FLAG, String(Date.now()));
    setStartedAt(Date.now());
    return () => {
      // Don't clear on unmount — completion clears it explicitly.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [required]);

  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [startedAt]);

  // Expiry → logout
  useEffect(() => {
    if (!startedAt) return;
    if (now - startedAt >= ROTATION_TTL_MS) {
      sessionStorage.removeItem(SESSION_FLAG);
      toast.error('Time expired — please log back in');
      logout();
    }
  }, [now, startedAt, logout]);

  // Block Escape key
  useEffect(() => {
    if (!required) return;
    const blockKeys = (e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); } };
    window.addEventListener('keydown', blockKeys, true);
    return () => window.removeEventListener('keydown', blockKeys, true);
  }, [required]);

  const submit = async () => {
    if (newPwd !== confirmPwd) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await api.post('/me/security/complete-password-rotation', {
        current_password: currentPwd,
        new_password: newPwd,
        confirm_password: confirmPwd,
        totp_code: totp || undefined,
      });
      sessionStorage.removeItem(SESSION_FLAG);
      toast.success('Password updated');
      await refreshUser();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update');
    } finally { setSubmitting(false); }
  };

  if (!user || !required) return null;

  const remaining = startedAt ? Math.max(0, Math.ceil((ROTATION_TTL_MS - (now - startedAt)) / 1000)) : 120;
  const mm = String(Math.floor(remaining / 60)).padStart(1, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  const isMaintenance = required?.reason === 'app_wide_maintenance';

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 p-4"
      style={{ backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)' }}
      data-testid="persistent-password-rotation-overlay"
    >
      <div className="w-full max-w-md rounded-2xl bg-background border border-red-500/40 shadow-2xl p-6 space-y-4">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-100 dark:bg-amber-900/40 p-2">
            {isMaintenance ? <Wrench className="w-5 h-5 text-amber-600 dark:text-amber-300" /> : <ShieldAlert className="w-5 h-5 text-red-600 dark:text-red-300" />}
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-base">{isMaintenance ? 'Maintenance Time!' : 'Password Reset Required'}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {isMaintenance ? 'Please reset your password to keep your account safe.' : 'An administrator requires you to change your password.'}
            </p>
          </div>
          <span className="text-[11px] font-mono font-bold text-red-600 dark:text-red-300" data-testid="pw-rotation-timer">
            {mm}:{ss}
          </span>
        </div>

        <div className="space-y-2">
          <Label className="text-xs flex items-center gap-1"><Lock className="w-3 h-3" /> Current Password</Label>
          <Input type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} data-testid="pw-rotation-current" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">New Password</Label>
          <Input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="pw-rotation-new" />
        </div>
        <div className="space-y-2">
          <Label className="text-xs">Confirm New Password</Label>
          <Input type="password" value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} data-testid="pw-rotation-confirm" />
        </div>
        {user?.totp_enabled && (
          <div className="space-y-2">
            <Label className="text-xs">Authenticator Code</Label>
            <Input
              inputMode="numeric"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="font-mono text-center tracking-widest"
              data-testid="pw-rotation-totp"
            />
          </div>
        )}
        <Button
          className="w-full bg-[#586D52] hover:bg-[#475a43]"
          onClick={submit}
          disabled={submitting || !currentPwd || !newPwd || newPwd !== confirmPwd || (user?.totp_enabled && totp.length !== 6)}
          data-testid="pw-rotation-submit"
        >
          {submitting ? 'Saving…' : 'Update Password'}
        </Button>
      </div>
    </div>
  );
}
